"use client";

import {
  IconBrandFacebook,
  IconBrandTelegram,
  IconCalendarEvent,
  IconChevronRight,
  IconMail,
  IconMapPin,
  IconPhone,
} from "@tabler/icons-react";
import { useEffect, useState, type ReactNode } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import {
  getGeneralSettingTranslation,
  GENERAL_SETTING_DEFAULTS,
  type GeneralSettingSection,
  type NavItem,
} from "@/app/(dashboard)/dashboard/webmaster/general-setting/general-setting-defaults";
import { Container } from "../ui/Container";
import { TikTokIcon } from "../ui/TikTokIcon";

const EXPO_START_DATE = "2026-07-17T00:00:00+07:00";

function getTimeLeft(targetDate: string) {
  const difference = new Date(targetDate).getTime() - Date.now();

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / (1000 * 60)) % 60),
    seconds: Math.floor((difference / 1000) % 60),
  };
}

export function FooterCountdown() {
  const locale = useLocale();
  const t = useTranslations("site.countdown");
  const tFooter = useTranslations("site.footerCountdown");
  const [timeLeft, setTimeLeft] = useState<ReturnType<typeof getTimeLeft> | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    // Synchronizes with the system clock; can't be computed during render
    // without causing a server/client hydration mismatch.
    setTimeLeft(getTimeLeft(EXPO_START_DATE));

    const interval = window.setInterval(() => {
      setTimeLeft(getTimeLeft(EXPO_START_DATE));
    }, 1000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isFinished =
    timeLeft !== null &&
    timeLeft.days === 0 &&
    timeLeft.hours === 0 &&
    timeLeft.minutes === 0 &&
    timeLeft.seconds === 0;

  const cells = timeLeft
    ? [
        { title: t("day"), value: timeLeft.days },
        { title: t("hour"), value: timeLeft.hours },
        { title: t("minute"), value: timeLeft.minutes },
        { title: t("second"), value: timeLeft.seconds },
      ]
    : [];

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-40 border-t border-[rgba(245,197,66,0.3)] bg-[rgba(5,13,25,0.92)] shadow-[0_-24px_60px_-32px_rgba(0,0,0,0.85)] backdrop-blur-xl transition duration-300 ${
        isScrolled ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
      }`}
      style={{ fontFamily: "var(--footer-body-font), sans-serif" }}
    >
      <div className="mx-auto flex max-w-[1180px] flex-col items-center gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-6">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[rgba(245,197,66,0.35)] bg-[rgba(245,197,66,0.12)] text-[#f5c542]">
            <IconCalendarEvent className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p
              className="truncate text-[0.85rem] font-semibold text-white"
              style={{ fontFamily: "var(--footer-body-font), var(--font-khmer), sans-serif" }}
            >
              {tFooter("title")}
            </p>
            <p
              className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-[#f5c542]"
              style={{ fontFamily: "var(--footer-body-font), var(--font-khmer), sans-serif" }}
            >
              {tFooter("dates")}
            </p>
          </div>
        </div>

        {timeLeft === null ? null : isFinished ? (
          <p className="text-sm font-semibold text-white">{tFooter("finished")}</p>
        ) : (
          <ul className="flex flex-wrap items-center justify-center gap-2">
            {cells.map((cell, index) => (
              <li key={cell.title} className="flex items-center gap-2">
                <div className="flex min-w-[2.8rem] flex-col items-center rounded-lg border border-[rgba(245,197,66,0.22)] bg-[rgba(255,255,255,0.03)] px-2.5 py-1.5">
                  <strong className="text-base font-black leading-none text-[#f5c542] sm:text-lg">
                    {cell.value.toLocaleString(locale === "km" ? "km-KH" : "en-US", {
                      minimumIntegerDigits: 2,
                    })}
                  </strong>
                  <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-white/60">
                    {cell.title}
                  </span>
                </div>
                {index !== cells.length - 1 ? (
                  <span className="hidden text-[#f5c542]/50 sm:inline">:</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const MAP_URL =
  "https://www.google.com/maps/search/?api=1&query=Diamond+Twin+Tower+Phnom+Penh";

// Section anchors (#about) only exist on the homepage, so point them there.
function toFooterHref(href: string) {
  return href.startsWith("#") ? `/${href}` : href;
}

// Top-level menu entries; a dropdown contributes its children instead.
function flattenNavItems(items: NavItem[]): NavItem[] {
  return items.flatMap((item) => (item.children?.length ? item.children : [item]));
}

function FooterHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-5 flex items-center gap-2.5 text-base font-bold text-white md:text-[1.05rem]">
      <span className="h-4 w-1 rounded-full bg-[#f6cf63]" aria-hidden="true" />
      {children}
    </h3>
  );
}

function ContactIcon({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/8 text-[#f6cf63]">
      {children}
    </span>
  );
}

export function FooterSection({ data: providedData }: { data?: GeneralSettingSection | null }) {
  const data = providedData ?? GENERAL_SETTING_DEFAULTS;
  const locale = useLocale();
  const t = useTranslations("site.footer");
  const generalT = getGeneralSettingTranslation(data, locale);
  const navLinks = flattenNavItems(generalT.header_nav_items).filter(
    (item) => ![
      "/invitation-announcement",
      "/instructions-participants",
      "/notice-announcement",
    ].includes(item.href),
  );
  const phones = [data.footer_phone1, data.footer_phone2].filter(Boolean);
  const socialLinks = [
    { href: data.footer_facebook_url, label: "Facebook", icon: <IconBrandFacebook className="h-4 w-4" /> },
    { href: data.footer_telegram_url, label: "Telegram", icon: <IconBrandTelegram className="h-4 w-4" /> },
    { href: data.footer_tiktok_url, label: "TikTok", icon: <TikTokIcon /> },
  ].filter((link) => Boolean(link.href));

  return (
    <footer id="contact" className="relative overflow-hidden bg-[#050f26] text-white">
      <div
        className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#bf1e2e_0%,#ffffff_50%,#f6cf63_100%)]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(246,207,99,0.08),transparent_45%),linear-gradient(180deg,#0a1f4d_0%,#050f26_100%)]"
        aria-hidden="true"
      />

      <Container className="relative">
        <div className="grid gap-10 py-14 sm:grid-cols-2 md:py-16 lg:grid-cols-[1.35fr_0.8fr_1fr] lg:gap-12">
          <section className="sm:col-span-2 lg:col-span-1">
            <Link href="/" className="inline-block text-2xl font-black tracking-wide text-white">
              MSME-EXPO<span className="text-[#f6cf63]">.COM</span>
            </Link>
            {generalT.footer_title ? (
              <p className="mt-2 text-lg font-bold leading-8 text-white">{generalT.footer_title}</p>
            ) : null}
            {generalT.footer_description ? (
              <p className="mt-3 max-w-md text-[0.95rem] leading-7 text-white/70">
                {generalT.footer_description}
              </p>
            ) : null}

            {socialLinks.length > 0 ? (
              <div className="mt-6">
                <p className="mb-3 text-sm font-semibold text-white/85">{t("followUs")}</p>
                <div className="flex gap-3">
                  {socialLinks.map(({ href, label, icon }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white transition duration-200 hover:-translate-y-0.5 hover:border-[#f6cf63] hover:bg-[#f6cf63] hover:text-[#071b4a]"
                    >
                      {icon}
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
          </section>

          {navLinks.length > 0 ? (
            <nav aria-label={t("quickLinks")}>
              <FooterHeading>{t("quickLinks")}</FooterHeading>
              <ul className="space-y-3">
                {navLinks.map((item) => (
                  <li key={`${item.href}-${item.label}`}>
                    <Link
                      href={toFooterHref(item.href)}
                      className="group inline-flex items-center gap-1.5 text-[0.95rem] text-white/70 transition hover:text-[#f6cf63]"
                    >
                      <IconChevronRight className="h-3.5 w-3.5 text-white/35 transition group-hover:translate-x-0.5 group-hover:text-[#f6cf63]" />
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ) : null}

          <section>
            <FooterHeading>{t("contact")}</FooterHeading>
            <ul className="space-y-4 text-[0.95rem] text-white/75">
              {phones.map((phone) => (
                <li key={phone}>
                  <a
                    href={`tel:${phone.replace(/\s+/g, "")}`}
                    className="flex items-center gap-3 transition hover:text-[#f6cf63]"
                  >
                    <ContactIcon>
                      <IconPhone className="h-4 w-4" />
                    </ContactIcon>
                    {phone}
                  </a>
                </li>
              ))}
              {data.footer_email ? (
                <li>
                  <a
                    href={`mailto:${data.footer_email}`}
                    className="flex items-center gap-3 break-all transition hover:text-[#f6cf63]"
                  >
                    <ContactIcon>
                      <IconMail className="h-4 w-4" />
                    </ContactIcon>
                    {data.footer_email}
                  </a>
                </li>
              ) : null}
              {data.footer_address ? (
                <li>
                  <a
                    href={MAP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 leading-7 transition hover:text-[#f6cf63]"
                  >
                    <ContactIcon>
                      <IconMapPin className="h-4 w-4" />
                    </ContactIcon>
                    {data.footer_address}
                  </a>
                </li>
              ) : null}
            </ul>
          </section>

        </div>
      </Container>

      <div className="relative border-t border-white/10 bg-[#030a1c]">
        <Container>
          <div className="flex flex-col items-center gap-3 py-5 text-center text-[0.82rem] leading-6 text-white/55 md:flex-row md:justify-between md:text-left">
            <div>
              {data.footer_copyright_text ? <p>{data.footer_copyright_text}</p> : null}
            </div>
          </div>
        </Container>
      </div>
    </footer>
  );
}
